import { formatFullWidthPunctuation } from './textFormatter';

/**
 * 前端图片合成工具
 * @description 在浏览器中使用Canvas API合成塔罗牌图片和AI分析文本
 * @author 数据管理系统开发团队
 * @date 2024
 */

export interface CompositeImageOptions {
  tarotImageUrl: string;
  llmResult: string;
  title?: string;
  templateId?: string;
  cardCount?: number;
  padding?: number;
  fontSize?: number;
  titleFontSize?: number;
  lineHeight?: number;
  textColor?: string;
  backgroundColor?: string;
}

export class ImageComposer {
  /**
   * 合成图片
   * @param options 合成选项
   * @returns Promise<Blob> 合成后的图片Blob
   */
  static async composeImage(options: CompositeImageOptions): Promise<Blob> {
    const {
      tarotImageUrl,
      llmResult,
      title = '',
      templateId = '',
      padding = 40,
      fontSize = 48, // 进一步增大字体
      titleFontSize = 72, // 标题字体大小，默认72px
      lineHeight = 72, // 相应增大行高
      textColor = '#2c3e50',
      backgroundColor = '#ffffff',
      cardCount = 0
    } = options;

    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous'; // 允许跨域

      img.onload = () => {
        try {
          // 固定合成图宽度为1280px
          const compositeWidth = 1280;
          const contentWidth = compositeWidth - (padding * 2); // 内容区域宽度

          // 计算图片缩放比例，使图片宽度适应内容区域
          const originalWidth = img.width;
          const originalHeight = img.height;
          const imageScale = contentWidth / originalWidth;
          const scaledImageWidth = contentWidth;
          const scaledImageHeight = originalHeight * imageScale;

          // 创建临时canvas来计算文本高度
          const tempCanvas = document.createElement('canvas');
          const tempCtx = tempCanvas.getContext('2d')!;
          tempCtx.font = `${fontSize}px "Microsoft YaHei", "PingFang SC", "Hiragino Sans GB", sans-serif`;

          // 计算标题高度（如果有标题）
          let titleHeight = 0;
          let titleLines: string[] = [];
          if (title && title.trim()) {
            const titleLineHeight = titleFontSize * 1.2;
            titleLines = ImageComposer.wrapTextFixed(title, 10);
            titleHeight = titleLines.length * titleLineHeight + padding;
          }

          // 计算每个字符的宽度，确保文字部分宽度与图片部分一致
          const textAreaWidth = contentWidth - 40; // 减去左右内边距（20px * 2）
          const charWidth = textAreaWidth / 20; // 每个字符的宽度，保持总宽度一致

          const formattedLlmResult = formatFullWidthPunctuation(llmResult);

          // 四季牌阵需要保留完整正文，其他模板保持原有按张数截取的逻辑
          const filteredLlmResult = templateId === 'four-seasons-spread'
            ? formattedLlmResult
            : ImageComposer.extractCardSections(formattedLlmResult, cardCount);

          // 固定每行20个中文字符的换行处理
          const lines = ImageComposer.wrapTextFixed(filteredLlmResult, 20);
          const textHeight = Math.max(lines.length * lineHeight + padding * 2, 200);
          
          // 添加温馨提示文字
          const tipText = '温馨提示\n关于这次解析我会实话实说地分享看到的情况，包括需要注意的地方，目的是帮助你更好地改善。\n但要记住，本次解析是基于你现在的状态和想法，不要把任何指引当成绝对的宿命，你随时可以通过调整心态和行动来改写结果。';
          const tipLines = ImageComposer.wrapTextFixed(tipText, 30); // 增加每行字符数到30
          const tipFontSize = 32; // 较小字体
          const tipLineHeight = 48; // 较小行高
          const tipHeight = tipLines.length * tipLineHeight + padding * 2;

          // 计算总高度：标题 + 图片 + 文本 + 温馨提示 + 间距
          const totalHeight = titleHeight + scaledImageHeight + textHeight + tipHeight + (padding * 5);

          console.log(`🖼️ 前端合成图片: ${compositeWidth}x${totalHeight}`);
          console.log(`📏 原图: ${originalWidth}x${originalHeight}, 缩放后: ${scaledImageWidth}x${scaledImageHeight}`);
          console.log(`📝 文本: ${lines.length}行, 高度: ${textHeight}px, 字符宽度: ${charWidth}px`);
          console.log(`💡 温馨提示: ${tipLines.length}行, 高度: ${tipHeight}px`);
          if (titleLines.length > 0) {
            console.log(`📋 标题: ${titleLines.length}行, 高度: ${titleHeight}px`);
          }

          // 创建主canvas
          const canvas = document.createElement('canvas');
          canvas.width = compositeWidth;
          canvas.height = totalHeight;
          const ctx = canvas.getContext('2d')!;

          // 设置背景
          ctx.fillStyle = backgroundColor;
          ctx.fillRect(0, 0, compositeWidth, totalHeight);

          let currentY = padding;

          // 绘制标题（如果有）
          if (titleLines.length > 0) {
            const titleLineHeight = titleFontSize * 1.2;
            const titleCharWidth = (contentWidth - 40) / 10;
            ImageComposer.drawTitleFixed(ctx, titleLines, padding, currentY, titleFontSize, titleLineHeight, textColor, titleCharWidth);
            currentY += titleHeight;
          }

          // 绘制塔罗牌图片（等比例缩放，居中）
          ctx.drawImage(img, padding, currentY, scaledImageWidth, scaledImageHeight);
          currentY += scaledImageHeight + padding;

          // 绘制文本，使用计算出的字符宽度
          ImageComposer.drawTextFixed(ctx, lines, padding, currentY, fontSize, lineHeight, textColor, charWidth);
          currentY += textHeight + padding;
          
          // 绘制温馨提示文字（灰色小字）
          const tipCharWidth = (contentWidth - 40) / 30; // 对应30个字符的宽度
          ImageComposer.drawTipTextFixed(ctx, tipLines, padding, currentY, tipFontSize, tipLineHeight, '#888888', tipCharWidth);

          // 转换为Blob
          canvas.toBlob((blob) => {
            if (blob) {
              console.log('✅ 前端图片合成完成');
              resolve(blob);
            } else {
              reject(new Error('Canvas转换为Blob失败'));
            }
          }, 'image/jpeg', 0.95);

        } catch (error) {
          console.error('图片合成失败:', error);
          reject(error);
        }
      };

      img.onerror = () => {
        reject(new Error('图片加载失败'));
      };

      img.src = tarotImageUrl;
    });
  }

  /**
   * 过滤AI分析结果，仅保留与牌阵张数相同的段落
   * @param text AI分析结果文本
   * @param cardCount 牌阵张数
   * @returns 过滤后的文本
   */
  private static extractCardSections(text: string, cardCount?: number): string {
    if (!text) {
      return text;
    }

    const normalizedCardCount = typeof cardCount === 'number' && cardCount > 0
      ? Math.floor(cardCount)
      : 0;

    if (normalizedCardCount <= 0) {
      return text;
    }

    const sections = text
      .split(/\n{2,}/)
      .map(section => section.trim())
      .filter(section => section.length > 0);

    if (sections.length === 0) {
      return text.trim();
    }

    const selectedSections = sections.slice(0, normalizedCardCount);
    return selectedSections.join('\n\n');
  }

  /**
   * 固定每行字符数的文本换行
   * @param text 文本内容
   * @param charsPerLine 每行字符数
   * @returns 换行后的文本数组
   */
  private static wrapTextFixed(text: string, charsPerLine: number): string[] {
    const lines: string[] = [];
    const paragraphs = text.split('\n');

    paragraphs.forEach(paragraph => {
      if (paragraph.trim() === '') {
        lines.push('');
        return;
      }

      let remainingText = paragraph;
      while (remainingText.length > 0) {
        if (remainingText.length <= charsPerLine) {
          lines.push(remainingText);
          break;
        }

        // 严格按照每行20个字符断行，不考虑标点符号
        const breakPoint = charsPerLine;
        lines.push(remainingText.slice(0, breakPoint));
        remainingText = remainingText.slice(breakPoint);
      }
    });

    return lines;
  }

  /**
   * 智能文本换行（备用方法）
   * @param ctx Canvas上下文
   * @param text 文本内容
   * @param maxWidth 最大宽度
   * @returns 换行后的文本数组
   */
  private static wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
    const lines: string[] = [];
    const paragraphs = text.split('\n');
    const effectiveWidth = maxWidth - 40; // 减去内边距

    paragraphs.forEach(paragraph => {
      if (paragraph.trim() === '') {
        lines.push('');
        return;
      }

      let remainingText = paragraph;
      while (remainingText.length > 0) {
        let currentLine = '';
        let bestBreakPoint = 0;
        
        // 逐字符测试，寻找最佳断点
        for (let i = 0; i < remainingText.length; i++) {
          const testLine = remainingText.slice(0, i + 1);
          const metrics = ctx.measureText(testLine);
          
          if (metrics.width <= effectiveWidth) {
            currentLine = testLine;
            bestBreakPoint = i + 1;
            
            // 如果遇到标点符号，优先在此处断行
            const punctuation = /[，。！？；：、]/;
            if (punctuation.test(remainingText[i])) {
              break;
            }
          } else {
            break;
          }
        }
        
        if (currentLine === '' && remainingText.length > 0) {
          // 如果一个字符都放不下，强制放一个字符
          currentLine = remainingText[0];
          bestBreakPoint = 1;
        }
        
        lines.push(currentLine);
        remainingText = remainingText.slice(bestBreakPoint);
      }
    });

    return lines;
  }

  /**
   * 绘制标题（使用固定字符宽度，加粗）
   * @param ctx Canvas上下文
   * @param lines 文本行数组
   * @param x X坐标
   * @param y Y坐标
   * @param fontSize 字体大小
   * @param lineHeight 行高
   * @param textColor 文字颜色
   * @param charWidth 每个字符的固定宽度
   */
  private static drawTitleFixed(
    ctx: CanvasRenderingContext2D,
    lines: string[],
    x: number,
    y: number,
    fontSize: number,
    lineHeight: number,
    textColor: string,
    charWidth: number
  ): void {
    ctx.font = `bold ${fontSize}px "Microsoft YaHei", "PingFang SC", "Hiragino Sans GB", sans-serif`;
    ctx.fillStyle = textColor;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';

    lines.forEach((line, index) => {
      const yPos = y + (index * lineHeight);

      // 左对齐，与图片宽度保持一致
      const startX = x + 20; // 添加内边距

      // 逐个字符绘制，确保每个字符占用固定宽度
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        const charX = startX + i * charWidth;
        ctx.fillText(char, charX, yPos);
      }
    });
  }

  /**
   * 绘制文本（使用固定字符宽度）
   * @param ctx Canvas上下文
   * @param lines 文本行数组
   * @param x X坐标
   * @param y Y坐标
   * @param fontSize 字体大小
   * @param lineHeight 行高
   * @param textColor 文字颜色
   * @param charWidth 每个字符的固定宽度
   */
  private static drawTextFixed(
    ctx: CanvasRenderingContext2D,
    lines: string[],
    x: number,
    y: number,
    fontSize: number,
    lineHeight: number,
    textColor: string,
    charWidth: number
  ): void {
    ctx.font = `${fontSize}px "Microsoft YaHei", "PingFang SC", "Hiragino Sans GB", sans-serif`;
    ctx.fillStyle = textColor;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';

    lines.forEach((line, index) => {
      const yPos = y + (index * lineHeight);

      // 左对齐，与图片宽度保持一致
      const startX = x + 20; // 添加内边距

      // 逐个字符绘制，确保每个字符占用固定宽度
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        const charX = startX + i * charWidth;
        ctx.fillText(char, charX, yPos);
      }
    });
  }

  /**
   * 绘制温馨提示文字（使用固定字符宽度，灰色小字）
   * @param ctx Canvas上下文
   * @param lines 文本行数组
   * @param x X坐标
   * @param y Y坐标
   * @param fontSize 字体大小
   * @param lineHeight 行高
   * @param textColor 文字颜色
   * @param charWidth 每个字符的固定宽度
   */
  private static drawTipTextFixed(
    ctx: CanvasRenderingContext2D,
    lines: string[],
    x: number,
    y: number,
    fontSize: number,
    lineHeight: number,
    textColor: string,
    charWidth: number
  ): void {
    ctx.font = `${fontSize}px "Microsoft YaHei", "PingFang SC", "Hiragino Sans GB", sans-serif`;
    ctx.fillStyle = textColor;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';

    lines.forEach((line, index) => {
      const yPos = y + (index * lineHeight);

      // 左对齐，与图片宽度保持一致
      const startX = x + 20; // 添加内边距

      // 逐个字符绘制，确保每个字符占用固定宽度
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        const charX = startX + i * charWidth;
        ctx.fillText(char, charX, yPos);
      }
    });
  }

  /**
   * 绘制文本（原方法保留）
   * @param ctx Canvas上下文
   * @param lines 文本行数组
   * @param x X坐标
   * @param y Y坐标
   * @param fontSize 字体大小
   * @param lineHeight 行高
   * @param textColor 文字颜色
   */
  private static drawText(
    ctx: CanvasRenderingContext2D,
    lines: string[],
    x: number,
    y: number,
    fontSize: number,
    lineHeight: number,
    textColor: string
  ): void {
    ctx.font = `${fontSize}px "Microsoft YaHei", "PingFang SC", "Hiragino Sans GB", sans-serif`;
    ctx.fillStyle = textColor;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';

    lines.forEach((line, index) => {
      const yPos = y + (index * lineHeight);
      ctx.fillText(line, x + 20, yPos); // 添加一些内边距
    });
  }

  /**
   * 下载合成图片
   * @param blob 图片Blob
   * @param filename 文件名
   */
  static downloadImage(blob: Blob, filename: string = `tarot_result_${Date.now()}.jpg`): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * 复制图片到剪贴板（改进版）
   * @param blob 图片Blob
   */
  static async copyToClipboard(blob: Blob): Promise<void> {
    // 1. 检查基础API支持
    if (!navigator.clipboard) {
      throw new Error('浏览器不支持剪贴板API');
    }

    if (!window.ClipboardItem) {
      throw new Error('浏览器不支持ClipboardItem');
    }

    // 2. 检查权限
    try {
      const permission = await navigator.permissions.query({ name: 'clipboard-write' as PermissionName });
      if (permission.state === 'denied') {
        throw new Error('剪贴板写入权限被拒绝');
      }
    } catch (permError) {
      console.warn('无法检查剪贴板权限:', permError);
      // 继续尝试，某些浏览器不支持权限查询
    }

    // 3. 确保图片格式正确
    let imageBlob = blob;
    
    // 如果不是标准图片格式，转换为PNG
    if (!blob.type || !blob.type.startsWith('image/')) {
      console.log('转换Blob类型为image/png');
      
      // 创建一个临时canvas来转换格式
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;
      
      const img = new Image();
      const url = URL.createObjectURL(blob);
      
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = url;
      });
      
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      
      imageBlob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((newBlob) => {
          if (newBlob) {
            resolve(newBlob);
          } else {
            reject(new Error('图片格式转换失败'));
          }
        }, 'image/png', 1.0);
      });
      
      URL.revokeObjectURL(url);
    }

    // 4. 尝试多种复制方式
    try {
      // 方式1: 标准方式
      await navigator.clipboard.write([
        new ClipboardItem({
          [imageBlob.type]: imageBlob
        })
      ]);
      console.log('✅ 使用标准方式复制成功');
      return;
    } catch (error1) {
      console.warn('标准复制方式失败:', error1);
      
      try {
        // 方式2: 强制使用PNG格式
        await navigator.clipboard.write([
          new ClipboardItem({
            'image/png': imageBlob
          })
        ]);
        console.log('✅ 使用PNG格式复制成功');
        return;
      } catch (error2) {
        console.warn('PNG格式复制失败:', error2);
        
        // 方式3: 尝试其他格式
        try {
          await navigator.clipboard.write([
            new ClipboardItem({
              'image/jpeg': imageBlob
            })
          ]);
          console.log('✅ 使用JPEG格式复制成功');
          return;
        } catch (error3) {
          console.error('所有复制方式都失败:', { error1, error2, error3 });
          throw new Error('复制失败：' + (error1 instanceof Error ? error1.message : String(error1)));
        }
      }
    }
  }

  /**
   * 创建预览URL
   * @param blob 图片Blob
   * @returns 预览URL
   */
  static createPreviewUrl(blob: Blob): string {
    return URL.createObjectURL(blob);
  }
}
